import { FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReportCollectionItem } from "@/types/api";

interface ItemCellProps {
  item: ReportCollectionItem;
  editing: boolean;
  onRemove: () => void;
  /**
   * A smaller remove control, for a table row: the whole row is a link, so the
   * control floats in its top-right corner, and at 24px it would crowd the
   * chevron centred below it. Table rows are for a pointer, so 20px is enough.
   */
  compact?: boolean;
  children: React.ReactNode;
}

/**
 * One entry of a report's contents: the item itself, dimmed when it has been
 * deleted from the project, with the remove control while the report is being
 * edited. See [[report-detail-page.spec]] US-003.
 */
export function ItemCell({
  item,
  editing,
  onRemove,
  compact = false,
  children,
}: ItemCellProps) {
  return (
    <div className={cn("relative", item.is_deleted && "opacity-60")}>
      {children}
      {editing && (
        <button
          type="button"
          aria-label="Remove from report"
          onClick={onRemove}
          className={cn(
            "absolute right-1 top-1 z-10 flex items-center justify-center rounded-full bg-background/90 shadow",
            compact ? "size-5" : "size-6",
          )}
        >
          <X className={compact ? "size-3" : "size-3.5"} />
        </button>
      )}
    </div>
  );
}

/** What stands in for an item that has been deleted from the project. */
export function Tombstone({ inline = false }: { inline?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 bg-muted text-xs text-muted-foreground",
        inline ? "min-h-16 p-3" : "aspect-square",
      )}
    >
      <FileText className="size-4" />
      Removed from project
    </div>
  );
}
